import { FormControl, FormHelperText, FormLabel, Grid, OutlinedInput, TextField } from '@mui/material';
import { FC } from 'react';
import { usePrompt } from '../../../Views';
import { CustomerSelect, SiteSelect } from '../../CommonInputs';
import { FormInput, LoadingIndicator } from '../../CoreLib/library';
import { ContactFormManager } from './useContactForm';

export interface IContactFormProps {
    contactFormManager: ContactFormManager;
    isLoading?: boolean;
}

export const ContactForm: FC<IContactFormProps> = ({ contactFormManager, isLoading }) => {
    const {
        fieldErrors,
        validateField,
        formFirstName,
        formLastName,
        formEmail,
        formTitle,
        formLandlinePhone,
        formCellPhone,
        formFax,
        formMemo,
        handleFirstNameChange,
        handleLastNameChange,
        handleEmailChange,
        handleTitleChange,
        handleLandlinePhoneChange,
        handleCellPhoneChange,
        handleFaxChange,
        handleMemoChange,
        customerId,
        siteId,
        isChanged,
    } = contactFormManager;

    usePrompt('Are you sure you want to leave this page?\nUnsaved changes will be lost.', isChanged);

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <Grid item container direction='row' spacing={3}>
                <Grid item xs={3}>
                    {customerId ? (
                        <CustomerSelect
                            selectedCustomerId={customerId}
                            handleSelectedCustomerChange={() => {}}
                            required
                            disabled
                        />
                    ) : (
                        <SiteSelect
                            selectedSiteId={siteId ?? ''}
                            handleSelectedSiteChange={() => { }}
                            required
                            errorMessage={fieldErrors.get('siteId')}
                            disabled
                        />
                    )}
                </Grid>
                <Grid item xs={3}>
                    <FormControl error={!!fieldErrors.get('firstName')} fullWidth required disabled={isLoading}>
                        <FormLabel>First Name</FormLabel>
                        <OutlinedInput value={formFirstName} onChange={handleFirstNameChange} onBlur={() => validateField('firstName')} />
                        <FormHelperText>{fieldErrors.get('firstName')}</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={isLoading}>
                        <FormLabel>Last Name</FormLabel>
                        <OutlinedInput value={formLastName} onChange={handleLastNameChange} onBlur={() => validateField('lastName')} />
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={isLoading}>
                        <FormLabel>Title</FormLabel>
                        <OutlinedInput value={formTitle} onChange={handleTitleChange} />
                    </FormControl>
                </Grid>
            </Grid>
            <Grid item container direction='row' spacing={3}>
                <Grid item xs={3}>
                    <FormControl error={!!fieldErrors.get('email')} fullWidth disabled={isLoading}>
                        <FormLabel>Email</FormLabel>
                        <OutlinedInput value={formEmail} onChange={handleEmailChange} onBlur={() => validateField('email')} />
                        <FormHelperText>{fieldErrors.get('email')}</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormInput
                        value={formLandlinePhone}
                        onChange={handleLandlinePhoneChange}
                        label='Landline Phone'
                        name='landlinePhone'
                        errorText={fieldErrors.get('landlinePhone')}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={3}>
                    <FormInput
                        value={formCellPhone}
                        onChange={handleCellPhoneChange}
                        label='Cell Phone'
                        name='cellPhone'
                        errorText={fieldErrors.get('cellPhone')}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={3}>
                    <FormInput value={formFax} onChange={handleFaxChange} label='Fax' name='fax' errorText={fieldErrors.get('fax')} fullWidth />
                </Grid>
            </Grid>
            <Grid item container direction='row' spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        value={formMemo}
                        onChange={handleMemoChange}
                        label='Memo'
                        name='memo'
                        error={!!fieldErrors.get('memo')}
                        helperText={fieldErrors.get('memo')}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </Grid>
            </Grid>
        </>
    );
};
